import Vue from 'vue'
import VueRouter, { Route } from 'vue-router'
import SignUp from '@/views/SignUp.vue'
import SignUpCallBack from '@/views/SignUpCallBack.vue'
import Login from '@/views/Login.vue'
import LoginCallBack from '@/views/LoginCallBack.vue'
import ChangeDepartmentConfirm from '@/views/ChangeDepartmentConfirm.vue'
import Upload from '@/views/Upload.vue'
import RegisterCodeRegistration from '@/views/RegisterCodeRegistration.vue'
import CustomerRegistration from '@/views/CustomerRegistration.vue'
import AgreementContract from '@/views/AgreementContract.vue'
import AgreementPreContract from '@/views/AgreementPreContract.vue'
import AgreementOther from '@/views/AgreementOther.vue'
import Complete from '@/views/Complete.vue'

import Error404 from '@/views/error/Error404.vue'
import Error403 from '@/views/error/Error403.vue'
import Error400 from '@/views/error/Error400.vue'
import SystemError from '@/views/error/SystemError.vue'
import store from '@/store'
import JWTUtils from '@/utils/JWTUtils'
import { registerCodeTypeMaster } from '@/utils/RegisterCodeType'

Vue.use(VueRouter)

/**
 * beforeEnter 規約同意：画面リロード時
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
function beforeEnterAgreementWhenReload (to: Route, from: Route, next: (path: string | void) => void): void {
  if (from.name === null) {
    // URL直打ち or リロードは、先頭の画面に遷移
    next('/agreement/contract')
    return
  }
  next()
}

/**
 * beforeEnter お客様情報登録：画面リロード時
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
function beforeEnterCustomerRegistrationWhenReload (to: Route, from: Route, next: (name: string | void) => void): void {
  if (from.name === null) {
    // URL直打ち or リロードは、登録コードの登録画面に遷移
    const companyCode = process.env.VUE_APP_COMPANY_CODE
    let registerCodeType
    for (const master of registerCodeTypeMaster) {
      if (master.key === companyCode) {
        registerCodeType = master.type
      }
    }
    if (registerCodeType) {
      next('registercoderegistration')
      return
    }
  }
  next()
}

/**
 * beforeEnter RegisterCode登録
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
function beforeEnterRegisterCodeRegistration (to: Route, from: Route, next: (name: string | void) => void): void {
  const companyCode = process.env.VUE_APP_COMPANY_CODE
  let registerCodeType
  for (const master of registerCodeTypeMaster) {
    if (master.key === companyCode) {
      registerCodeType = master.type
    }
  }
  // 提携先別に登録コードの登録画面を表示するか、スキップしてお客様情報画面を表示するか切り分け
  if (registerCodeType) {
    next()
    return
  }
  next('/customerregistration')
}

const routes = [
  {
    path: '/',
    redirect: '/signUp'
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/login/callback',
    name: 'loginCallBack',
    component: LoginCallBack,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/signup',
    name: 'signUp',
    component: SignUp,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/signup/callback',
    name: 'signUpCallBack',
    component: SignUpCallBack,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/changeDepartmentConfirm',
    name: 'changeDepartmentConfirm',
    component: ChangeDepartmentConfirm
  },
  {
    path: '/upload',
    name: 'upload',
    component: Upload
  },
  {
    path: '/registercoderegistration',
    name: 'registercoderegistration',
    component: RegisterCodeRegistration,
    beforeEnter: beforeEnterRegisterCodeRegistration
  },
  {
    path: '/customerregistration',
    name: 'customerregistration',
    component: CustomerRegistration,
    beforeEnter: beforeEnterCustomerRegistrationWhenReload
  },
  {
    path: '/agreement/contract',
    name: 'agreementContract',
    component: AgreementContract
  },
  {
    path: '/agreement/precontract',
    name: 'agreementPreContract',
    component: AgreementPreContract,
    beforeEnter: beforeEnterAgreementWhenReload
  },
  {
    path: '/agreement/other',
    name: 'agreementOther',
    component: AgreementOther,
    beforeEnter: beforeEnterAgreementWhenReload
  },
  {
    path: '/complete',
    name: 'complete',
    component: Complete
  },
  {
    path: '/error/500',
    name: 'systemError',
    component: SystemError,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/error/400',
    name: 'badRequest',
    component: Error400,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/error/403',
    name: 'forbidden',
    component: Error403,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '/error/404',
    name: 'notFound',
    component: Error404,
    meta: {
      ignoreAuth: true
    }
  },
  {
    path: '*',
    name: 'notFoundPage',
    component: Error404,
    meta: {
      ignoreAuth: true
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
router.beforeEach((to, from, next) => {
  // ログインチェック
  if (to.matched.some(record => !record.meta || !record.meta.ignoreAuth) && !store.getters.isLogined) {
    // ログインが必要で、ログインしていない場合
    const jwtToken = JWTUtils.get() // localStorage の JWT確認
    if (jwtToken) {
      const { companyId } = JWTUtils.getSubject(jwtToken)
      if (companyId === process.env.VUE_APP_COMPANY_ID) {
        store.dispatch('loginByJwt', { jwtToken }).then(nextLocation => {
          next(nextLocation)
        })
      } else {
        // 別会社の認証情報でアクセスされた場合は、loginページへ
        store.commit('loginError', true)
        next({ path: '/login' })
      }
    } else {
      // loginページへ
      next({ path: '/login' })
    }
  } else {
    next()
  }
})

export default router
